// File: accountingFirestoreService.ts (updated)
import { 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  getDocs,
  orderBy,
  addDoc,
  serverTimestamp,
  Timestamp,
  DocumentData,
  QuerySnapshot,
  limit as firestoreLimit
} from 'firebase/firestore';
import { db } from './config';
import { cache } from '../utils/cacheManager';

export const ACCOUNTING_COLLECTIONS = {
  ACCOUNTING: 'accounting',
  ACCOUNTING_CATEGORIES: 'accountingCategories',
  VENDORS: 'vendors'
};

export interface Accounting {
  id?: string;
  accountingId: string;
  description: string;
  amount: number;
  currency: string;
  category: string;
  paymentMethod: string;
  vendor?: string;
  receiptNumber?: string;
  date: string;
  status: 'Pending' | 'Approved' | 'Paid' | 'Rejected';
  approvedBy?: string;
  approvedAt?: any;
  notes?: string;
  attachments?: string[];
  createdAt?: any;
  updatedAt?: any;
  createdBy?: string;
}

export interface AccountingCategory {
  id?: string;
  name: string;
  description?: string;
  color: string;
  icon?: string;
  type: 'Income' | 'Expense' | 'Transfer';
  createdAt?: any;
  updatedAt?: any;
}

export interface Vendor {
  id?: string;
  name: string;
  contactPerson?: string;
  email?: string;
  phone?: string;
  address?: string;
  taxId?: string;
  category?: string;
  paymentTerms?: string;
  notes?: string;
  createdAt?: any;
  updatedAt?: any;
}

// Stable cache key helpers
export const cacheKeys = {
  recordsRange: (start: string, end: string) => `accounting.records.${start}.${end}`,
  recordsAll: 'accounting.records.all',
  categories: 'accounting.categories',
  vendors: 'accounting.vendors'
};

// Helper to normalize various date representations to a YYYY-MM-DD string
const normalizeDate = (value: any): string => {
  try {
    if (!value) return '';
    // Firestore Timestamp
    if (typeof value === 'object' && typeof (value as any).toDate === 'function') {
      return (value as any).toDate().toISOString().split('T')[0];
    }
    // ISO string with time or date-only
    if (typeof value === 'string') {
      return value.split('T')[0];
    }
    // Date object
    if (value instanceof Date) {
      return value.toISOString().split('T')[0];
    }
    return String(value);
  } catch (e) {
    return String(value);
  }
};

// Generic response type
interface FirestoreResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  id?: string;
}

// Accounting Records CRUD
export const getAccountingRecords = async (limitCount: number = 300): Promise<FirestoreResponse<Accounting[]>> => {
  try {
    const q = query(
      collection(db, ACCOUNTING_COLLECTIONS.ACCOUNTING),
      orderBy('createdAt', 'desc'),
      firestoreLimit(limitCount)
    );
    const snapshot = await getDocs(q);
    const data = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    })).map((d: any) => ({
      ...d,
      date: normalizeDate(d.date)
    })) as Accounting[];
    
    return { success: true, data };
  } catch (error) {
    console.error('Error fetching accounting records:', error);
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
};

export const getAccountingRecord = async (id: string): Promise<FirestoreResponse<Accounting>> => {
  try {
    const docRef = doc(db, ACCOUNTING_COLLECTIONS.ACCOUNTING, id);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const raw = { id: docSnap.id, ...docSnap.data() } as any;
      raw.date = normalizeDate(raw.date);
      return { success: true, data: raw as Accounting };
    } else {
      return { success: false, error: 'Record not found' };
    }
  } catch (error) {
    console.error('Error fetching accounting record:', error);
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
};

export const createAccountingRecord = async (record: Accounting): Promise<FirestoreResponse<string>> => {
  try {
    const toSave: any = { ...record };
    if (toSave.date) toSave.date = normalizeDate(toSave.date);
    const docRef = await addDoc(collection(db, ACCOUNTING_COLLECTIONS.ACCOUNTING), {
      ...toSave,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    // Invalidate records cache after creating a new record
    try { cache.invalidatePattern(/^accounting\.records/); } catch (e) { /* ignore */ }
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('Error creating accounting record:', error);
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
};

export const updateAccountingRecord = async (
  id: string, 
  updates: Partial<Accounting>
): Promise<FirestoreResponse<void>> => {
  try {
    const docRef = doc(db, ACCOUNTING_COLLECTIONS.ACCOUNTING, id);
    const toUpdate: any = { ...updates };
    if (toUpdate.date) toUpdate.date = normalizeDate(toUpdate.date);
    await updateDoc(docRef, {
      ...toUpdate,
      updatedAt: serverTimestamp()
    });
    // Invalidate records cache after updating
    try { cache.invalidatePattern(/^accounting\.records/); } catch (e) { /* ignore */ }
    return { success: true };
  } catch (error) {
    console.error('Error updating accounting record:', error);
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
};

export const deleteAccountingRecord = async (id: string): Promise<FirestoreResponse<void>> => {
  try {
    const docRef = doc(db, ACCOUNTING_COLLECTIONS.ACCOUNTING, id);
    await deleteDoc(docRef);
    // Invalidate records cache after deletion
    try { cache.invalidatePattern(/^accounting\.records/); } catch (e) { /* ignore */ }
    return { success: true };
  } catch (error) {
    console.error('Error deleting accounting record:', error);
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
};

// Categories CRUD
export const getAccountingCategories = async (): Promise<FirestoreResponse<AccountingCategory[]>> => {
  try {
    const q = query(
      collection(db, ACCOUNTING_COLLECTIONS.ACCOUNTING_CATEGORIES),
      orderBy('name')
    );
    const snapshot = await getDocs(q);
    const data = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as AccountingCategory[];
    
    return { success: true, data };
  } catch (error) {
    console.error('Error fetching categories:', error);
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
};

export const createAccountingCategory = async (
  category: AccountingCategory
): Promise<FirestoreResponse<string>> => {
  try {
    const docRef = await addDoc(collection(db, ACCOUNTING_COLLECTIONS.ACCOUNTING_CATEGORIES), {
      ...category,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    // Invalidate categories cache
    try { cache.invalidate(cacheKeys.categories); } catch (e) { /* ignore */ }
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('Error creating category:', error);
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
};

// Vendors CRUD
export const getVendors = async (): Promise<FirestoreResponse<Vendor[]>> => {
  try {
    const q = query(
      collection(db, ACCOUNTING_COLLECTIONS.VENDORS),
      orderBy('name')
    );
    const snapshot = await getDocs(q);
    const data = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as Vendor[];
    
    return { success: true, data };
  } catch (error) {
    console.error('Error fetching vendors:', error);
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
};

export const createVendor = async (vendor: Vendor): Promise<FirestoreResponse<string>> => {
  try {
    const docRef = await addDoc(collection(db, ACCOUNTING_COLLECTIONS.VENDORS), {
      ...vendor,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    // Invalidate vendors cache
    try { cache.invalidate(cacheKeys.vendors); } catch (e) { /* ignore */ }
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('Error creating vendor:', error);
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
};

// Dashboard Statistics
export const getAccountingStatistics = async (): Promise<FirestoreResponse<any>> => {
  try {
    const q = query(collection(db, ACCOUNTING_COLLECTIONS.ACCOUNTING));
    const snapshot = await getDocs(q);
    const records = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })).map((d: any) => ({
      ...d,
      date: normalizeDate(d.date)
    })) as Accounting[];
    
    const today = new Date().toISOString().split('T')[0];
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    
    const todayRecords = records.filter(r => r.date === today);
    const currentMonthRecords = records.filter(r => {
      const recordDate = new Date(r.date);
      return recordDate.getMonth() === currentMonth && recordDate.getFullYear() === currentYear;
    });
    
    const stats = {
      totalTransactions: records.length,
      totalAmount: records.reduce((sum, r) => sum + r.amount, 0),
      pendingAmount: records.filter(r => r.status === 'Pending').reduce((sum, r) => sum + r.amount, 0),
      approvedAmount: records.filter(r => r.status === 'Approved').reduce((sum, r) => sum + r.amount, 0),
      paidAmount: records.filter(r => r.status === 'Paid').reduce((sum, r) => sum + r.amount, 0),
      rejectedAmount: records.filter(r => r.status === 'Rejected').reduce((sum, r) => sum + r.amount, 0),
      todayTransactions: todayRecords.length,
      todayAmount: todayRecords.reduce((sum, r) => sum + r.amount, 0),
      monthlyTransactions: currentMonthRecords.length,
      monthlyAmount: currentMonthRecords.reduce((sum, r) => sum + r.amount, 0)
    };
    
    return { success: true, data: stats };
  } catch (error) {
    console.error('Error fetching statistics:', error);
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
};

// Get records by date range
export const getAccountingRecordsByDateRange = async (
  startDate: string,
  endDate: string,
  limitCount: number = 500
): Promise<FirestoreResponse<Accounting[]>> => {
  try {
    const q = query(
      collection(db, ACCOUNTING_COLLECTIONS.ACCOUNTING),
      where('date', '>=', startDate),
      where('date', '<=', endDate),
      orderBy('date', 'desc'),
      firestoreLimit(limitCount)
    );
    const snapshot = await getDocs(q);
    const data = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })).map((d: any) => ({ ...d, date: normalizeDate(d.date) })) as Accounting[];
    
    return { success: true, data };
  } catch (error) {
    console.error('Error fetching records by date range:', error);
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
};

// Get records by status
export const getAccountingRecordsByStatus = async (
  status: string,
  limitCount: number = 300
): Promise<FirestoreResponse<Accounting[]>> => {
  try {
    const q = query(
      collection(db, ACCOUNTING_COLLECTIONS.ACCOUNTING),
      where('status', '==', status),
      orderBy('createdAt', 'desc'),
      firestoreLimit(limitCount)
    );
    const snapshot = await getDocs(q);
    const data = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as Accounting[];
    
    return { success: true, data };
  } catch (error) {
    console.error('Error fetching records by status:', error);
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
};
// In accountingFirestoreService.ts, add these functions:

export const updateAccountingCategory = async (
  id: string,
  updates: Partial<AccountingCategory>
): Promise<FirestoreResponse<void>> => {
  try {
    const docRef = doc(db, ACCOUNTING_COLLECTIONS.ACCOUNTING_CATEGORIES, id);
    await updateDoc(docRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });
    // Invalidate categories cache
    try { cache.invalidate(cacheKeys.categories); } catch (e) { /* ignore */ }
    return { success: true };
  } catch (error) {
    console.error('Error updating category:', error);
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
};

export const deleteAccountingCategory = async (id: string): Promise<FirestoreResponse<void>> => {
  try {
    const docRef = doc(db, ACCOUNTING_COLLECTIONS.ACCOUNTING_CATEGORIES, id);
    await deleteDoc(docRef);
    // Invalidate categories cache
    try { cache.invalidate(cacheKeys.categories); } catch (e) { /* ignore */ }
    return { success: true };
  } catch (error) {
    console.error('Error deleting category:', error);
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
};

export const updateVendor = async (
  id: string,
  updates: Partial<Vendor>
): Promise<FirestoreResponse<void>> => {
  try {
    const docRef = doc(db, ACCOUNTING_COLLECTIONS.VENDORS, id);
    await updateDoc(docRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });
    // Invalidate vendors cache
    try { cache.invalidate(cacheKeys.vendors); } catch (e) { /* ignore */ }
    return { success: true };
  } catch (error) {
    console.error('Error updating vendor:', error);
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
};

export const deleteVendor = async (id: string): Promise<FirestoreResponse<void>> => {
  try {
    const docRef = doc(db, ACCOUNTING_COLLECTIONS.VENDORS, id);
    await deleteDoc(docRef);
    // Invalidate vendors cache
    try { cache.invalidate(cacheKeys.vendors); } catch (e) { /* ignore */ }
    return { success: true };
  } catch (error) {
    console.error('Error deleting vendor:', error);
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
};
